import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { getAllPosts, Post } from '../utils/blogLoader';
import ScrollToTop from '../components/ScrollToTop';
import {
  PostCard,
  PostImage,
  PostContent,
  PostTitle,
  PostExcerpt,
  PostMeta,
  PostDate,
  PostTags,
  Tag,
  ReadMore
} from '../styles/components';

const HomeContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Banner = styled.section`
  display: flex;
  align-items: center;
  min-height: 80vh;
  background-color: ${({ theme }) => theme.surfaceAlt};
  margin: -2rem -2rem 2rem -2rem;
  padding: 2rem;
`;

const BannerContent = styled.div`
  flex: 1;
  max-width: 600px;
`;

const BannerImage = styled.div`
  flex: 1;
  img {
    width: 100%;
    height: auto;
  }
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.text};
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.textSecondary};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled.a`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.surface};
  text-decoration: none;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    filter: brightness(0.92);
  }
`;

const PostsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 2rem 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeaturesSection = styled.section`
  padding: 4rem 0;
  background-color: ${({ theme }) => theme.surfaceAlt};
  margin-top: 3rem;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${({ theme }) => theme.text};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 2rem;
  background: ${({ theme }) => theme.surface};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadow};
`;

const FeatureIcon = styled.span`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 1rem;
  display: block;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.text};
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.6;
`;

const Home: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadPosts = async () => {
      try {
        const allPosts = await getAllPosts();
        setPosts(allPosts.slice(0, 3));
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  const features = [
    {
      id: 1,
      icon: 'fa-gem',
      title: 'Modern Design',
      description: 'Clean and modern design that focuses on user experience and readability.'
    },
    {
      id: 2,
      icon: 'fa-code',
      title: 'Responsive',
      description: 'Fully responsive design that works perfectly on all devices and screen sizes.'
    },
    {
      id: 3,
      icon: 'fa-rocket',
      title: 'Fast Performance',
      description: 'Optimized for speed and performance to provide the best user experience.'
    }
  ];

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <HomeContainer>
      <Banner>
        <BannerContent>
          <Title>Welcome to My Blog</Title>
          <Subtitle>
            A place to share thoughts, ideas, and experiences about technology, development, and more.
          </Subtitle>
          <Button href="#posts">Read Latest Posts</Button>
        </BannerContent>
        <BannerImage>
          <img src="https://images.unsplash.com/photo-1499750310107-5fef28a66643" alt="Banner" />
        </BannerImage>
      </Banner>

      <PostsGrid id="posts">
        {posts.map((post) => (
          <PostCard key={post.slug} onClick={() => navigate(`/blog/${post.slug}`)}>
            <PostImage>
              <img src={post.image} alt={post.title} />
            </PostImage>
            <PostContent>
              <PostTitle>{post.title}</PostTitle>
              <PostMeta>
                By {post.author} • {new Date(post.date).toLocaleDateString()}
              </PostMeta>
              <PostExcerpt>{post.excerpt}</PostExcerpt>
              {post.tags.length > 0 && (
                <PostTags>
                  {post.tags.map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </PostTags>
              )}
            </PostContent>
          </PostCard>
        ))}
      </PostsGrid>

      <FeaturesSection>
        <SectionTitle>Why Choose This Blog</SectionTitle>
        <FeaturesGrid>
          {features.map((feature) => (
            <FeatureCard key={feature.id}>
              <FeatureIcon>
                <i className={`fas ${feature.icon}`}></i>
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>
      <ScrollToTop />
    </HomeContainer>
  );
};

export default Home; 